import React, { ForwardedRef, forwardRef, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { useDispatch } from 'react-redux';

import { Info } from 'assets/assetsV1/iconComponents';

import { roundOffToPlaces } from 'constants/helper';

import { Divider } from 'uiToolkitV1/Divider';
import { ProgressBar } from 'uiToolkitV1/ProgressBar';
import { SelectV2 } from 'uiToolkitV1/SelectV2';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TagOutlined } from 'uiToolkitV1/Tag/TagOutlined';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import {
  setProgressLabel,
  setProgressScore,
  useCompetencyDrawerState,
  useProgressLabel,
} from '../competencyDrawerSlice';
import { CheckinButton } from '../components/CheckinButton';
import { Comment } from '../components/Comment';
import { SectionContainer } from '../components/SectionContainer';
import { useGetCompetencyScoreMutation, useGetLevelUpDetailsQuery } from '../dataHooks';
import { useHasDataChanged } from '../helpers/useHasDataChanged';
import { ProgressKeys } from '../types/slice';

export const Checkin = forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
  const progressKey = useProgressLabel();
  const { progressScore } = useCompetencyDrawerState();
  const hasDataChanged = useHasDataChanged();
  const dispatch = useDispatch();

  const { data: levelUpCompetencyDetails, isLoading } = useGetLevelUpDetailsQuery();
  const { mutate: getCompetencyScore, isLoading: isScoreLoading } = useGetCompetencyScoreMutation();

  const menuItems = (levelUpCompetencyDetails?.progressStages ?? []).map((filter, i) => {
    const previousItem = levelUpCompetencyDetails.progressStages[i - 1] ?? { progressPercentage: 0 };
    return {
      id: filter.id,
      label:
        i === 0
          ? `${filter.progress} (${previousItem.progressPercentage})`
          : `${filter.progress} (${roundOffToPlaces(Number(previousItem.progressPercentage) + 0.1, 2)} - ${
              filter.progressPercentage
            })`,
      value: filter.progress,
    };
  });

  const selectedItem = menuItems.find((item) => item.value === progressKey) ?? menuItems[0];

  const lastActivity = levelUpCompetencyDetails?.activity?.[levelUpCompetencyDetails.activity.length - 1];

  const handleProgressChange = (item) => {
    dispatch(setProgressLabel(item.value));
    getCompetencyScore({ progressId: item.id });
  };

  useEffect(() => {
    if (levelUpCompetencyDetails?.progress) {
      dispatch(setProgressLabel(levelUpCompetencyDetails?.progress as ProgressKeys));
    }
  }, [dispatch, levelUpCompetencyDetails?.progress]);

  useEffect(() => {
    if (
      levelUpCompetencyDetails?.progressPercentage !== undefined &&
      levelUpCompetencyDetails?.progressPercentage !== null
    ) {
      dispatch(setProgressScore(levelUpCompetencyDetails?.progressPercentage));
    }
  }, [dispatch, levelUpCompetencyDetails?.progressPercentage]);

  return (
    <SectionContainer ref={ref}>
      <TextBodyMedium bold mt={2} mb={4} mx={3}>
        Check-in
      </TextBodyMedium>
      <Divider borderColor="#CFD7DF" />
      <Flex
        flexDir="column"
        borderRadius="8px"
        border="1px solid #EBEFF3"
        bgColor="#FDFDFD"
        p="16px 12px"
        gap={4}
        alignItems="flex-start"
        mt={3}
      >
        <Flex flexDir="column" w="100%">
          <TextLabel color="text.g2">Level</TextLabel>
          <SelectV2
            menuItems={menuItems}
            value="notStarted"
            onSelect={handleProgressChange}
            isReadOnly={!levelUpCompetencyDetails?.isDetailAccessible}
            selectedItem={selectedItem}
            // @ts-expect-error Tag expects more props
            renderMenuItem={(item) => <TagOutlined onClick={() => {}}>{item?.label}</TagOutlined>}
          />
          <TextLabel color="text.g3" mt={1}>
            Choose a level to update the progress for the competency
          </TextLabel>
        </Flex>
        <Flex flexDir="column" w="100%" gap={1}>
          <TextLabel color="text.g2">Progress</TextLabel>
          {isLoading ? (
            <SkeletonLoader />
          ) : (
            <Flex alignItems="flex-start" gap={2}>
              {isScoreLoading ? (
                <SkeletonLoader />
              ) : (
                <>
                  <Flex flexDir="column" gap={1} w="100%">
                    {/* @ts-expect-error ProgressBar expects more props */}
                    <ProgressBar
                      size="large"
                      color="#DBF2D3"
                      backgroundColor="#CFD7DF"
                      value={progressScore}
                      max={levelUpCompetencyDetails.maxScore}
                      height="24px"
                    />
                    <Flex justifyContent="space-between">
                      <TextLabelSmall color="text.g2">0</TextLabelSmall>
                      <TextLabelSmall color="text.g2">{levelUpCompetencyDetails.maxScore}</TextLabelSmall>
                    </Flex>
                  </Flex>
                  <TextBodyMedium bold whiteSpace="nowrap">
                    {progressScore}
                  </TextBodyMedium>
                </>
              )}
            </Flex>
          )}
        </Flex>
        {levelUpCompetencyDetails?.isDetailAccessible && <Comment />}
        <Flex alignItems="center" w="100%">
          <Flex flexDir="column" gap={1}>
            {lastActivity && (
              <Flex alignItems="center">
                <Info color="text.g2" />
                <TextLabel color="text.g2">
                  Last check-in:{' '}
                  {formatDistanceToNow(lastActivity.createdTS, {
                    addSuffix: true,
                  })}
                </TextLabel>
              </Flex>
            )}
            {hasDataChanged && (
              <TextLabel color="functional.AF5C20" ml={1}>{`Click 'Check-in' to save your changes`}</TextLabel>
            )}
          </Flex>
          <Box ml="auto">
            <CheckinButton />
          </Box>
        </Flex>
      </Flex>
    </SectionContainer>
  );
});
